import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Heart, Pill, UtensilsCrossed, Users, Home, Moon, Clock, 
  ArrowRight, CheckCircle, Phone 
} from "lucide-react";
import { Button } from "../components/ui/button";
import { services, companyInfo } from "../data/mockData";

export const Services = () => {
  const iconMap = {
    Heart: Heart,
    Pill: Pill,
    UtensilsCrossed: UtensilsCrossed,
    Users: Users,
    Home: Home,
    Moon: Moon,
    Clock: Clock,
  };

  return (
    <div data-testid="services-page" className="pt-24">
      {/* Hero Section */}
      <section className="py-16 relative">
        <div className="absolute top-0 left-0 w-96 h-96 bg-secondary/20 rounded-full blur-3xl -z-10" />
        
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-xs tracking-widest uppercase font-bold text-primary/60 mb-4 block">
              Our Services
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-primary leading-tight mb-6">
              Comprehensive Home Care Services
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              We offer a wide range of domiciliary care services tailored to meet 
              your unique needs, ensuring you receive the support you deserve in 
              the comfort of your own home.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="section-container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Heart;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  data-testid={`service-card-${service.id}`}
                  className="bg-white border border-primary/10 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif font-semibold text-primary text-xl mb-3">
                      {service.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="text-xs tracking-widest uppercase font-bold text-primary/60 mb-3 block">
              Getting Started
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-primary mb-4">
              How Our Care Process Works
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              We make arranging care simple and stress-free with our straightforward process.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Initial Contact",
                description: "Call us or fill out our contact form to discuss your needs.",
              },
              {
                step: "02",
                title: "Free Assessment",
                description: "We visit you at home to understand your requirements.",
              },
              {
                step: "03",
                title: "Care Plan",
                description: "We create a personalized care plan tailored to you.",
              },
              {
                step: "04",
                title: "Care Begins",
                description: "Your matched carer starts providing support.",
              },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`process-step-${index}`}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-serif font-bold text-2xl text-primary">{item.step}</span>
                </div>
                <h3 className="font-serif font-semibold text-primary text-lg mb-2">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs tracking-widest uppercase font-bold text-primary/60 mb-4 block">
                Why Choose Us
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-primary mb-6">
                Quality Care You Can Trust
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                At Konastone Healthcare, we go above and beyond to ensure every 
                client receives exceptional care that makes a real difference.
              </p>
              <ul className="space-y-4">
                {[
                  "CQC registered and regularly inspected",
                  "Fully trained, DBS-checked care professionals",
                  "Personalized care plans for each individual",
                  "Flexible scheduling to suit your lifestyle",
                  "24/7 on-call support for peace of mind",
                  "Regular reviews and care plan updates",
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://images.unsplash.com/photo-1576602976047-174e57a47881?w=600"
                alt="Quality care"
                className="w-full h-[450px] object-cover rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mb-4">
              Ready to Discuss Your Care Needs?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Contact our friendly team today for a free, no-obligation care assessment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button
                  data-testid="services-cta-btn"
                  className="bg-accent hover:bg-accent/90 text-white rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105 w-full sm:w-auto"
                >
                  Book Free Assessment
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href={`tel:${companyInfo.phone}`}>
                <Button
                  data-testid="services-call-btn"
                  variant="outline"
                  className="bg-transparent text-white border-2 border-white/30 hover:border-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-medium transition-all w-full sm:w-auto"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  Call Now
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
